import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, 
  Camera, 
  CheckCircle2, 
  User, 
  MapPin, 
  AlertCircle, 
  Loader2,
  Truck,
  Package,
  Users,
  ClipboardList,
  LogOut
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate a simple device fingerprint
const generateDeviceId = () => {
  const stored = localStorage.getItem('security_device_id');
  if (stored) return stored;
  
  const fingerprint = `DEV-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('security_device_id', fingerprint);
  return fingerprint;
};

export default function SecurityCheckinPage() {
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [faceApiLoaded, setFaceApiLoaded] = useState(false);
  const [activeCheckin, setActiveCheckin] = useState(null);
  const [securityStaff, setSecurityStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [cameraActive, setCameraActive] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    security_person_id: '',
    security_person_name: '',
    unit_location_id: '',
    unit_location_name: ''
  });
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  const deviceId = generateDeviceId();

  // Check for existing valid check-in
  useEffect(() => {
    const checkExistingCheckin = async () => {
      try {
        const response = await axios.get(`${API_URL}/store/security-checkin/validate?device_id=${deviceId}`);
        if (response.data.valid) {
          setActiveCheckin(response.data.checkin);
          // Store in localStorage for gate entry page
          localStorage.setItem('security_checkin', JSON.stringify(response.data.checkin));
        }
      } catch (error) {
        console.error('Error checking existing check-in:', error);
      }
      setInitializing(false);
    };
    
    checkExistingCheckin();
  }, [deviceId]);

  // Load security staff and locations
  useEffect(() => {
    const loadData = async () => {
      try {
        const [staffRes, locRes] = await Promise.all([
          axios.get(`${API_URL}/store/security-staff`),
          axios.get(`${API_URL}/store/locations-public`)
        ]);
        setSecurityStaff(staffRes.data);
        setLocations(locRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load data');
      }
    };
    
    loadData();
  }, []);

  // Load face-api.js from CDN
  useEffect(() => {
    const loadFaceApi = async () => {
      if (window.faceapi) {
        setFaceApiLoaded(true);
        return;
      }
      
      // Set a timeout - if face API doesn't load in 10 seconds, proceed without it
      const timeout = setTimeout(() => {
        console.log('Face API load timeout - proceeding without face detection');
        setFaceApiLoaded(true);
      }, 10000);
      
      try {
        // Load face-api.js
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/dist/face-api.min.js';
        script.async = true;
        
        script.onload = async () => {
          try {
            // Load models from CDN
            const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';
            await window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
            clearTimeout(timeout);
            setFaceApiLoaded(true);
            toast.success('Face detection ready');
          } catch (modelError) {
            console.error('Error loading face models:', modelError);
            clearTimeout(timeout);
            setFaceApiLoaded(true); // Proceed without face detection
            toast.info('Proceeding without face detection');
          }
        };
        
        script.onerror = () => {
          clearTimeout(timeout);
          toast.error('Failed to load face detection. Proceeding without it.');
          setFaceApiLoaded(true); // Allow proceeding without face detection
        };
        
        document.body.appendChild(script);
      } catch (error) {
        console.error('Error loading face-api:', error);
        clearTimeout(timeout);
        setFaceApiLoaded(true); // Allow proceeding
      }
    };
    
    loadFaceApi();
  }, []);

  // Start camera
  const startCamera = async () => {
    setCameraActive(true); // Show camera UI immediately
    
    try {
      // Simpler constraints for better Android compatibility
      const constraints = { 
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      };
      
      console.log('Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Camera stream obtained:', stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // Wait for video to be ready before playing
        videoRef.current.onloadedmetadata = async () => {
          console.log('Video metadata loaded');
          try {
            await videoRef.current.play();
            console.log('Video playing');
          } catch (playError) {
            console.error('Play error:', playError);
          }
        };
        
        // Start face detection loop (if available)
        if (window.faceapi) {
          detectionIntervalRef.current = setInterval(async () => {
            if (videoRef.current && videoRef.current.readyState === 4) {
              try {
                const detections = await window.faceapi.detectAllFaces(
                  videoRef.current, 
                  new window.faceapi.TinyFaceDetectorOptions()
                );
                setFaceDetected(detections.length >= 1);
              } catch (detectError) {
                // Ignore detection errors, allow capture anyway
                setFaceDetected(true);
              }
            }
          }, 500);
        } else {
          // If face-api not loaded, allow capture anyway
          setFaceDetected(true);
        }
      }
    } catch (error) {
      console.error('Camera error:', error);
      setCameraActive(false);
      toast.error(`Camera error: ${error.message || 'Please allow camera permission'}`);
    }
  };

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    setCameraActive(false);
    setFaceDetected(false);
  }, []);

  // Capture selfie
  const captureSelfie = () => {
    if (!faceDetected) {
      toast.error('Please position your face in the camera');
      return;
    }
    
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // Get image as data URL (but we won't store it)
      const imageData = canvas.toDataURL('image/jpeg', 0.5);
      setCapturedImage(imageData);
      
      stopCamera();
      toast.success('Photo captured successfully');
    }
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.security_person_id || !formData.unit_location_id) {
      toast.error('Please select your name and location');
      return;
    }
    
    if (!capturedImage) {
      toast.error('Please take a selfie for verification');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        security_person_id: formData.security_person_id,
        security_person_name: formData.security_person_name,
        unit_location_id: formData.unit_location_id,
        unit_location_name: formData.unit_location_name,
        device_id: deviceId
      };
      
      const response = await axios.post(`${API_URL}/store/security-checkin`, payload);
      
      if (response.data.success) {
        const checkin = response.data.checkin;
        setActiveCheckin(checkin);
        localStorage.setItem('security_checkin', JSON.stringify(checkin));
        toast.success('Check-in successful!');
      }
    } catch (error) {
      console.error('Error during check-in:', error);
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setLoading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Navigate to gate entry
  const goToGateEntry = () => {
    window.location.href = '/gate-entry';
  };

  // Navigate to gate pass
  const goToGatePass = () => {
    window.location.href = '/security/gate-pass';
  };

  // Navigate to employee movement
  const goToEmployeeMovement = () => {
    window.location.href = '/security/employee-movement';
  };

  // Navigate to register
  const goToRegister = () => {
    window.location.href = '/gate-entry/register';
  };

  // Handle checkout
  const handleCheckout = async () => {
    if (!window.confirm('Are you sure you want to checkout? This will end your current session.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/store/security-checkout`, {
        device_id: deviceId,
        reason: 'shift_end'
      });
      
      if (response.data.success) {
        // Store checkout info before clearing
        setCheckoutInfo({
          name: activeCheckin?.security_person_name,
          location: activeCheckin?.unit_location_name,
          entriesCount: response.data.entries_count,
          checkoutTime: new Date().toLocaleTimeString()
        });
        
        localStorage.removeItem('security_checkin');
        setActiveCheckin(null);
        setCheckoutComplete(true);  // Show success screen instead of check-in form
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
              <p className="text-muted-foreground">Checking session...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show checkout success screen
  if (checkoutComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-green-200">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-6">
              {/* Success Icon */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              
              {/* Success Message */}
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-green-700">Checkout Successful!</h2>
                <p className="text-muted-foreground">Your shift has ended. Thank you for your service.</p>
              </div>
              
              {/* Summary */}
              {checkoutInfo && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-left">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">{checkoutInfo.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{checkoutInfo.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{checkoutInfo.entriesCount} entries recorded during shift</span>
                  </div>
                  <div className="text-xs text-muted-foreground pt-2 border-t">
                    Checkout Time: {checkoutInfo.checkoutTime}
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-3 pt-4">
                <Button 
                  onClick={() => {
                    setCheckoutComplete(false);
                    setCheckoutInfo(null);
                  }}
                  className="w-full h-12"
                >
                  <Shield className="w-5 h-5 mr-2" />
                  Start New Check-In
                </Button>
                <p className="text-xs text-muted-foreground">
                  You can close this page or start a new shift
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show security dashboard if already checked in
  if (activeCheckin) {
    const validUntil = new Date(activeCheckin.valid_until);
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white p-4">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Header Card */}
          <Card className="border-green-200 bg-white">
            <CardContent className="pt-6">
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-green-700">Security Dashboard</h2>
                
                <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-left">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">{activeCheckin.security_person_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{activeCheckin.unit_location_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Session valid until: {validUntil.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Gate Entry IN */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow border-blue-200 hover:border-blue-400"
              onClick={goToGateEntry}
            >
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Truck className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-blue-900">Gate Entry</h3>
                <p className="text-xs text-muted-foreground">Incoming Material</p>
              </CardContent>
            </Card>

            {/* Gate Pass OUT */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow border-orange-200 hover:border-orange-400"
              onClick={goToGatePass}
            >
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Package className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-orange-900">Gate Pass</h3>
                <p className="text-xs text-muted-foreground">Outgoing Material</p>
              </CardContent>
            </Card>

            {/* Employee Movement */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow border-purple-200 hover:border-purple-400"
              onClick={goToEmployeeMovement}
            >
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-purple-900">Employee</h3>
                <p className="text-xs text-muted-foreground">Movement Tracking</p>
              </CardContent>
            </Card>

            {/* View Register */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow border-slate-200 hover:border-slate-400"
              onClick={goToRegister}
            >
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ClipboardList className="w-6 h-6 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Register</h3>
                <p className="text-xs text-muted-foreground">Today's Entries</p>
              </CardContent>
            </Card>
          </div>

          {/* Checkout Button */}
          <Card className="border-red-200">
            <CardContent className="p-4">
              <Button 
                onClick={handleCheckout} 
                variant="outline"
                className="w-full h-12 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <LogOut className="w-5 h-5 mr-2" />
                )}
                End Shift / Checkout
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Security Check-In</h1>
          <p className="text-muted-foreground">Daily verification before gate entry access</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Your Details
            </CardTitle>
            <CardDescription>
              Select your name, location, and take a selfie for verification
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Security Person Selection */}
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Select 
                  value={formData.security_person_id}
                  onValueChange={(value) => {
                    const person = securityStaff.find(s => s.id === value);
                    setFormData({ 
                      ...formData, 
                      security_person_id: value,
                      security_person_name: person?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your name" />
                  </SelectTrigger>
                  <SelectContent>
                    {securityStaff.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No security staff found
                      </SelectItem>
                    ) : (
                      securityStaff.map(person => (
                        <SelectItem key={person.id} value={person.id}>
                          {person.name} {person.employee_code && `(${person.employee_code})`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {securityStaff.length === 0 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    No staff in Security department. Contact admin.
                  </p>
                )}
              </div>

              {/* Unit Location Selection */}
              <div className="space-y-2">
                <Label>Unit Location *</Label>
                <Select 
                  value={formData.unit_location_id}
                  onValueChange={(value) => {
                    const location = locations.find(l => l.id === value);
                    setFormData({ 
                      ...formData, 
                      unit_location_id: value,
                      unit_location_name: location?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(location => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name} ({location.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selfie Section */}
              <div className="space-y-3">
                <Label>Live Selfie Verification *</Label>
                
                {/* Debug info - remove in production */}
                <div className="text-xs text-muted-foreground bg-slate-50 p-2 rounded">
                  Face API: {faceApiLoaded ? '✓ Ready' : 'Loading...'} | 
                  Camera: {cameraActive ? '✓ Active' : 'Not started'} |
                  Face: {faceDetected ? '✓ Detected' : 'Not detected'}
                </div>
                
                {!capturedImage ? (
                  <div className="border rounded-lg overflow-hidden bg-slate-900">
                    {!cameraActive ? (
                      <div className="aspect-video flex flex-col items-center justify-center bg-slate-100">
                        <Camera className="w-12 h-12 text-slate-400 mb-2" />
                        <Button type="button" onClick={startCamera}>
                          Start Camera
                        </Button>
                        {!faceApiLoaded && (
                          <p className="text-xs text-muted-foreground mt-2">
                            (Face detection loading... camera will work without it)
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <video 
                          ref={videoRef} 
                          autoPlay 
                          playsInline 
                          muted 
                          className="w-full aspect-video object-cover"
                        />
                        <canvas ref={canvasRef} className="hidden" />
                        
                        {/* Face detection indicator */}
                        <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                          faceDetected 
                            ? 'bg-green-500 text-white' 
                            : 'bg-red-500 text-white'
                        }`}>
                          {faceDetected ? '✓ Face Detected' : '✗ No Face'}
                        </div>
                        
                        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                          <Button 
                            type="button" 
                            onClick={captureSelfie}
                            disabled={!faceDetected}
                            className="bg-white text-slate-900 hover:bg-slate-100"
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Capture
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={stopCamera}
                            className="bg-white/80"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="relative">
                      <img 
                        src={capturedImage} 
                        alt="Captured selfie" 
                        className="w-full aspect-video object-cover"
                      />
                      <div className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium bg-green-500 text-white">
                        ✓ Photo Captured
                      </div>
                    </div>
                    <div className="p-2 bg-slate-50 flex justify-center">
                      <Button type="button" variant="outline" size="sm" onClick={retakePhoto}>
                        Retake Photo
                      </Button>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  Position your face clearly in the camera. Photo is used for verification only and is not stored.
                </p>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full h-12 text-lg" 
                disabled={loading || !capturedImage || !formData.security_person_id || !formData.unit_location_id}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Complete Check-In
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Check-in valid until 06:00 AM next day
        </p>
      </div>
    </div>
  );
}
